import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Sparkles, Moon, Sun, Dumbbell, Briefcase, Flame, Shield, Heart, Leaf, Repeat, Award, Timer, Compass, Brain, Apple, Zap, Mountain } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import BotanicalIcon from "@/components/BotanicalIcon";

/* ------------------------------------------------------------------ */
/*  Motion primitives — same pattern used elsewhere on the site        */
/* ------------------------------------------------------------------ */
const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
};

const Reveal = ({ children, delay = 0, className = "", as: Tag = "div", testId }) => {
  const [ref, shown] = useReveal();
  return (
    <Tag
      ref={ref}
      data-testid={testId}
      className={className}
      style={{
        transform: shown ? "translateY(0)" : "translateY(28px)",
        opacity: shown ? 1 : 0,
        transition: `transform 900ms cubic-bezier(0.2,0.7,0.15,1) ${delay}ms, opacity 900ms ease ${delay}ms`,
      }}
    >
      {children}
    </Tag>
  );
};

const SectionEyebrow = ({ n, label, dark = false }) => (
  <div className="flex items-center gap-4">
    <div className={"serif italic text-[15px] " + (dark ? "text-[var(--hk-gold)]" : "text-[var(--hk-gold-deep)]")}>{n}</div>
    <div className={"w-10 h-px " + (dark ? "bg-[var(--hk-gold)]/50" : "bg-[var(--hk-border)]")} />
    <div className={"text-[11px] tracking-[0.42em] uppercase " + (dark ? "text-[var(--hk-gold)]" : "text-[var(--hk-gold-deep)]")}>{label}</div>
  </div>
);

const Grain = () => (
  <div
    aria-hidden="true"
    className="absolute inset-0 opacity-[0.16] mix-blend-overlay pointer-events-none"
    style={{
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.9  0 0 0 0 0.8  0 0 0 0 0.6  0 0 0 0.28 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
    }}
  />
);

/* ------------------------------------------------------------------ */
/*  360 Philosophy — 12-node interactive wheel                         */
/* ------------------------------------------------------------------ */
const WHEEL = [
  { label: "Strength",                headline: "Physical Strength",              body: "Botanicals traditionally associated with steady, day-long resilience.",       Icon: Dumbbell },
  { label: "Everyday Vitality",       headline: "Everyday Vitality",              body: "Sustained energy for demanding modern schedules — not stimulant-driven.",     Icon: Zap },
  { label: "Intimate Wellness",       headline: "Men's Intimate Wellness",        body: "Ayurveda's respected botanicals for men's vitality, presented with dignity.", Icon: Heart },
  { label: "Bodybuilding",            headline: "Natural Bodybuilding",           body: "A quiet daily companion to a disciplined training lifestyle.",                Icon: Mountain },
  { label: "Recovery",                headline: "Deep Recovery",                  body: "The body grows in the rest, not in the reps. This ritual belongs there.",    Icon: Timer },
  { label: "Mental Focus",            headline: "Mental Focus",                   body: "Composure and clarity for long thinking, without the caffeine crash.",       Icon: Brain },
  { label: "Modern Professional",     headline: "Modern Professional Life",       body: "Built for founders, executives and men who run long, demanding days.",       Icon: Briefcase },
  { label: "Confidence",              headline: "Quiet Confidence",               body: "The confidence that comes from a body cared for consistently.",              Icon: Flame },
  { label: "Long-Term Wellness",      headline: "Long-Term Men's Wellness",       body: "A slow, cumulative philosophy — measured in years, not weeks.",              Icon: Shield },
  { label: "Nutrition",               headline: "Nutrition Companion",            body: "A considered addition beside honest food, adequate protein and hydration.",   Icon: Apple },
  { label: "Night Ritual",            headline: "Night Recovery Ritual",          body: "Milk, ghee and MAN 360 — the deliberate close of a well-lived day.",         Icon: Moon },
  { label: "Botanical Wisdom",        headline: "Ayurvedic Botanical Wisdom",     body: "13 carefully selected botanicals drawn from the classical Rasayana library.", Icon: Leaf },
];

const PhilosophyWheel = () => {
  const [active, setActive] = useState(null); // index of clicked node
  return (
    <div className="relative mx-auto" style={{ width: "min(720px, 96vw)", height: "min(720px, 96vw)" }}>
      {/* Concentric engraved rings */}
      <div className="absolute inset-0 rounded-full border border-[var(--hk-border)]" />
      <div className="absolute inset-[6%] rounded-full border border-[var(--hk-border)]" />
      <div className="absolute inset-[16%] rounded-full border border-[var(--hk-gold)]/45" />
      <div className="absolute inset-[30%] rounded-full border border-[var(--hk-gold)]/35" />

      {/* Radial spokes + connecting lines from centre to each node */}
      <svg className="absolute inset-0 pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id="wheel-spoke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(217,117,53,0.55)" />
            <stop offset="100%" stopColor="rgba(201,169,97,0.10)" />
          </linearGradient>
        </defs>
        {WHEEL.map((_, i) => {
          const angle = (i / WHEEL.length) * Math.PI * 2 - Math.PI / 2;
          const x2 = 50 + 42 * Math.cos(angle);
          const y2 = 50 + 42 * Math.sin(angle);
          const isActive = active === i;
          return (
            <line
              key={i}
              x1="50"
              y1="50"
              x2={x2}
              y2={y2}
              stroke={isActive ? "url(#wheel-spoke)" : "rgba(168,137,74,0.22)"}
              strokeWidth={isActive ? "0.5" : "0.22"}
              strokeDasharray={isActive ? "0 0" : "0.6 0.6"}
              style={{ transition: "all 400ms ease" }}
            />
          );
        })}
      </svg>

      {/* Central gold sun with brand statement */}
      <div className="absolute inset-[32%] rounded-full flex items-center justify-center bg-white shadow-[0_30px_60px_-24px_rgba(30,35,31,0.45)] border border-[var(--hk-gold)]/60"
           style={{ background: "radial-gradient(circle at 30% 30%, #FFFDF7, #F5EFE4)" }}>
        <div className="text-center px-3">
          <div className="serif italic text-[13px] text-[var(--hk-gold-deep)]" style={{ paddingBottom: "0.06em" }}>— the —</div>
          <div className="serif font-medium text-[42px] sm:text-[54px] leading-[0.95] text-[var(--hk-orange-deep)]" style={{ paddingBottom: "0.04em" }}>
            MAN 360
          </div>
          <div className="mt-2 text-[9.5px] tracking-[0.32em] uppercase text-[var(--hk-gold-deep)]" style={{ paddingBottom: "0.06em" }}>
            Complete Men's Wellness
          </div>
        </div>
      </div>

      {/* 12 outer nodes */}
      {WHEEL.map((item, i) => {
        const angle = (i / WHEEL.length) * Math.PI * 2 - Math.PI / 2;
        const cx = 50 + 44 * Math.cos(angle);
        const cy = 50 + 44 * Math.sin(angle);
        const { Icon, label, headline, body } = item;
        const isActive = active === i;
        return (
          <button
            key={label}
            type="button"
            onClick={() => setActive(isActive ? null : i)}
            className="absolute -translate-x-1/2 -translate-y-1/2 group cursor-pointer focus:outline-none hk-fade-up"
            style={{
              left: `${cx}%`,
              top: `${cy}%`,
              animationDelay: `${300 + i * 80}ms`,
            }}
            data-testid={`man360-wheel-node-${i}`}
            aria-label={`${headline}: ${body}`}
          >
            <div
              className={
                "w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center mx-auto transition-all duration-500 " +
                (isActive
                  ? "bg-[var(--hk-orange)] border border-[var(--hk-orange-deep)] text-white scale-110 shadow-[0_12px_30px_-8px_rgba(217,117,53,0.55)]"
                  : "bg-white border border-[var(--hk-gold)]/50 text-[var(--hk-orange-deep)] shadow-[0_8px_20px_-8px_rgba(30,35,31,0.24)] group-hover:scale-110 group-hover:border-[var(--hk-orange)]/70 group-hover:shadow-[0_12px_28px_-8px_rgba(217,117,53,0.4)] group-active:scale-95")
              }
            >
              <Icon size={22} strokeWidth={1.4} />
            </div>
            <div
              className={
                "mt-3 whitespace-nowrap text-[10px] tracking-[0.22em] uppercase transition-colors text-center " +
                (isActive ? "text-[var(--hk-orange-deep)]" : "text-[var(--hk-ink-2)] group-hover:text-[var(--hk-orange-deep)]")
              }
              style={{ paddingBottom: "0.06em" }}
            >
              {label}
            </div>
          </button>
        );
      })}

      {/* Active-node detail card (appears in the center bottom on click) */}
      {active !== null && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+20px)] w-[min(360px,90%)] p-5 rounded-2xl bg-white border border-[var(--hk-gold)]/50 shadow-[0_20px_50px_-20px_rgba(30,35,31,0.35)] hk-fade-up"
          data-testid="man360-wheel-active-detail"
        >
          <div className="label-eyebrow">— {WHEEL[active].label}</div>
          <h4 className="serif mt-2 text-[20px] leading-snug text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>
            {WHEEL[active].headline}
          </h4>
          <p className="mt-2 text-[13.5px] leading-[1.75] text-[var(--hk-ink-2)]">
            {WHEEL[active].body}
          </p>
        </div>
      )}
    </div>
  );
};

/* ================================================================== */
/*  MAN 360 dedicated story                                            */
/* ================================================================== */
const Man360Story = () => {
  return (
    <div data-testid="man360-story">

      {/* ============================================================ */}
      {/* SECTION 1 — Built for Modern Men (dark cinematic intro)       */}
      {/* ============================================================ */}
      <section className="relative py-32 lg:py-44 overflow-hidden bg-[var(--hk-ink)] text-[#EDE4CE]" data-testid="man360-modern-men">
        <div
          aria-hidden="true"
          className="absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full blur-[140px] opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(217,117,53,0.55), transparent 70%)" }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-56 -right-40 w-[720px] h-[720px] rounded-full blur-[140px] opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(201,169,97,0.55), transparent 70%)" }}
        />
        <Grain />

        <div className="relative mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <SectionEyebrow n="I." label="Built for Modern Men" dark />
          </Reveal>
          <Reveal delay={120}>
            <h2 className="serif mt-10 text-[44px] sm:text-[64px] lg:text-[88px] leading-[1.08] tracking-[-0.02em] text-white font-medium" style={{ paddingBottom: "0.06em" }}>
              A quiet daily discipline —
              <br />
              <span className="italic text-[var(--hk-orange)]">for the man who wants more.</span>
            </h2>
          </Reveal>
          <Reveal delay={300}>
            <p className="mt-12 text-[18px] leading-[2] text-white/80 max-w-3xl">
              MAN 360 is a premium Ayurvedic herbal granulated formulation, created for men living
              modern, demanding lives — men who balance careers, businesses, families, fitness
              goals and personal responsibilities without asking for shortcuts.
            </p>
          </Reveal>
          <Reveal delay={420}>
            <p className="mt-6 text-[18px] leading-[2] text-white/75 max-w-3xl">
              It is not a quick fix. It is not a performance drink. It is a considered daily
              rasayana — a slow, disciplined wellness ritual that sits alongside your training,
              your nutrition, your sleep and your working life. Every one of them a little more
              deliberate. Every one of them a little more supported.
            </p>
          </Reveal>

          <Reveal delay={640} className="mt-16 grid sm:grid-cols-3 gap-5">
            {[
              { i: <Briefcase size={18} strokeWidth={1.6} />, l: "For the disciplined professional." },
              { i: <Dumbbell size={18} strokeWidth={1.6} />, l: "For the man who trains." },
              { i: <Compass size={18} strokeWidth={1.6} />, l: "For the man who plays the long game." },
            ].map((c, i) => (
              <div
                key={i}
                className="p-5 rounded-2xl border border-white/12 bg-white/[0.03] backdrop-blur-sm flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-white/5 border border-white/12 flex items-center justify-center text-[var(--hk-gold)]">{c.i}</div>
                <div className="serif italic text-[16px] leading-snug text-white/85">{c.l}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 2 — Men's Intimate Wellness (editorial, understated)  */}
      {/* ============================================================ */}
      <section className="relative py-32 lg:py-44 overflow-hidden" data-testid="man360-intimate">
        <BotanicalBackdrop variant="sideLeft" />
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5 md:sticky md:top-32">
              <Reveal>
                <SectionEyebrow n="II." label="Men's Intimate Wellness" />
              </Reveal>
              <Reveal delay={120}>
                <h2 className="serif mt-8 text-[38px] sm:text-[52px] lg:text-[66px] leading-[1.08] tracking-tight text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>
                  A tradition that <span className="italic-accent text-[var(--hk-orange-deep)]">honours</span> the whole man.
                </h2>
              </Reveal>
              <Reveal delay={280}>
                <p className="mt-8 text-[16px] leading-[1.95] text-[var(--hk-ink-2)] max-w-md">
                  Ayurveda has always spoken about men's vitality with dignity — as a natural
                  outcome of a life lived in balance, not as a promise to be sold.
                </p>
              </Reveal>
            </div>

            <div className="md:col-span-7 space-y-8">
              <Reveal delay={100}>
                <p className="text-[17px] leading-[2] text-[var(--hk-ink-2)]">
                  For centuries, Ayurveda has respected a considered group of botanicals for their
                  supportive role in men's vitality and reproductive wellness — not in isolation,
                  but as part of a wider, patient way of living. Shilajit from the Himalayas.
                  Ashwagandha, the composed adaptogen. Safed Musli. Gokshura. Kauch Beej. Each one
                  studied, revered and used in tradition alongside good sleep, honest food and
                  physical discipline.
                </p>
              </Reveal>
              <Reveal delay={220}>
                <p className="text-[17px] leading-[2] text-[var(--hk-ink-2)]">
                  MAN 360 draws thoughtfully from that library. It respects the tradition it
                  comes from and refuses the language of miracles. We will not promise you an
                  overnight transformation. We will say, honestly, that these botanicals have
                  long belonged in a considered daily routine — and we have taken care to bring
                  that routine into the modern life you actually live.
                </p>
              </Reveal>
              <Reveal delay={340}>
                <div className="pl-6 border-l-2 border-[var(--hk-gold)]">
                  <p className="serif text-[22px] sm:text-[26px] leading-[1.4] text-[var(--hk-ink)] italic" style={{ paddingBottom: "0.06em" }}>
                    Considered, not clinical.
                    <br />
                    Traditional, not old-fashioned.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 3 — Natural Strength & Active Lifestyle (dark/forest) */}
      {/* ============================================================ */}
      <section
        className="relative py-32 lg:py-44 overflow-hidden text-[#EDE4CE]"
        style={{ backgroundColor: "#1F2A22" }}
        data-testid="man360-strength"
      >
        <div
          aria-hidden="true"
          className="absolute -top-40 -right-40 w-[680px] h-[680px] rounded-full blur-[140px] opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(217,117,53,0.5), transparent 70%)" }}
        />
        <Grain />

        <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-6">
              <Reveal>
                <SectionEyebrow n="III." label="Natural Strength · Active Lifestyle" dark />
              </Reveal>
              <Reveal delay={120}>
                <h2 className="serif mt-10 text-[40px] sm:text-[56px] lg:text-[76px] leading-[1.08] tracking-[-0.02em] text-white font-medium" style={{ paddingBottom: "0.06em" }}>
                  The gym is just <span className="italic text-[var(--hk-orange)]">one part</span> of it.
                </h2>
              </Reveal>
              <Reveal delay={280}>
                <p className="mt-10 text-[18px] leading-[2] text-white/80">
                  Every man who has trained seriously knows this. Strength is not built under the
                  bar. Strength is built in the hours <em>after</em> the bar — in the food, the
                  sleep, the patience, the recovery. In the invisible discipline nobody claps for.
                </p>
              </Reveal>
              <Reveal delay={400}>
                <p className="mt-6 text-[18px] leading-[2] text-white/75">
                  MAN 360 was formulated as a botanical companion to that quieter half of
                  training. Not a supplement to <em>replace</em> the work — a considered nightly
                  ritual to sit beside it.
                </p>
              </Reveal>
            </div>

            <div className="md:col-span-6">
              <Reveal delay={200}>
                <div className="text-[11px] tracking-[0.42em] uppercase text-[var(--hk-gold)] mb-6">Strength is made of five things</div>
              </Reveal>
              <div className="space-y-4">
                {[
                  { i: <Dumbbell size={18} />, t: "Training", b: "Consistent progressive load — the visible half." },
                  { i: <Leaf size={18} />, t: "Nutrition", b: "Honest food, adequate protein, real ingredients." },
                  { i: <Moon size={18} />, t: "Sleep", b: "The most under-rated performance enhancer in the world." },
                  { i: <Timer size={18} />, t: "Recovery", b: "The body grows in the rest, not in the reps." },
                  { i: <Repeat size={18} />, t: "Consistency", b: "Small, boring, daily. Over months and years." },
                ].map((row, i) => (
                  <Reveal key={row.t} delay={200 + i * 90}>
                    <div className="flex items-start gap-5 p-5 rounded-2xl border border-white/12 bg-white/[0.03] backdrop-blur-sm">
                      <div className="w-11 h-11 rounded-full bg-[var(--hk-orange)]/15 border border-[var(--hk-orange)]/30 text-[var(--hk-orange)] flex items-center justify-center shrink-0">
                        {row.i}
                      </div>
                      <div>
                        <div className="serif text-[19px] text-white leading-snug">{row.t}</div>
                        <div className="mt-1 text-[14px] text-white/70 leading-[1.7]">{row.b}</div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 4 — Everyday Vitality (bright, professional)          */}
      {/* ============================================================ */}
      <section className="relative py-32 lg:py-44 overflow-hidden bg-[var(--hk-warm-ivory)]" data-testid="man360-vitality">
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <Reveal>
                <SectionEyebrow n="IV." label="Everyday Vitality" />
              </Reveal>
              <Reveal delay={120}>
                <h2 className="serif mt-8 text-[38px] sm:text-[54px] lg:text-[72px] leading-[1.08] tracking-tight text-[var(--hk-ink)]" style={{ paddingBottom: "0.06em" }}>
                  For the men who run <span className="italic-accent text-[var(--hk-orange-deep)]">long days.</span>
                </h2>
              </Reveal>
              <Reveal delay={280}>
                <p className="mt-8 text-[18px] leading-[2] text-[var(--hk-ink-2)] max-w-2xl">
                  Corporate professionals. Founders. Entrepreneurs. Business owners. Men whose
                  calendars fill before the sun is up and empty long after it is down. The ones
                  who don't get to be tired.
                </p>
              </Reveal>
              <Reveal delay={400}>
                <p className="mt-6 text-[18px] leading-[2] text-[var(--hk-ink-2)] max-w-2xl">
                  Sustained energy is not something you can borrow from a can of caffeine or a
                  weekend of rest. It is something you build — slowly, over months, from the
                  small, unglamorous rituals you refuse to skip. MAN 360 is designed to be one of
                  them.
                </p>
              </Reveal>
            </div>

            <div className="md:col-span-5">
              <Reveal delay={220}>
                <div className="hk-card p-8 lg:p-10">
                  <div className="text-[11px] tracking-[0.32em] uppercase text-[var(--hk-gold-deep)]">The Long-Days Kit</div>
                  <div className="mt-5 space-y-4">
                    {[
                      "Consistency over stimulation.",
                      "Composure over caffeine.",
                      "Endurance built by the year, not the hour.",
                      "A calm body, in a busy life.",
                    ].map((line, i) => (
                      <Reveal key={i} delay={280 + i * 90}>
                        <div className="flex items-start gap-3">
                          <LeafGlyph className="mt-1 text-[var(--hk-orange-deep)] w-4 h-4 shrink-0" />
                          <div className="serif text-[19px] leading-snug text-[var(--hk-ink)]">{line}</div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 5 — Why MAN 360? (four principles)                    */}
      {/* ============================================================ */}
      <section className="relative py-32 lg:py-44 overflow-hidden" data-testid="man360-why">
        <BotanicalBackdrop variant="sideRight" />
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
          <div className="max-w-3xl">
            <Reveal>
              <SectionEyebrow n="V." label="Why MAN 360" />
            </Reveal>
            <Reveal delay={120}>
              <h2 className="serif mt-8 text-[40px] sm:text-[58px] lg:text-[78px] leading-[1.08] tracking-tight text-[var(--hk-ink)]" style={{ paddingBottom: "0.06em" }}>
                Ingredient lists are easy.
                <br />
                <span className="italic-accent text-[var(--hk-orange-deep)]">Conviction is not.</span>
              </h2>
            </Reveal>
            <Reveal delay={280}>
              <p className="mt-8 text-[17px] leading-[2] text-[var(--hk-ink-2)]">
                We could publish the botanicals and call it a product. We chose to publish the
                thinking too. Because a considered formulation should come with a considered
                answer to <em>why</em>.
              </p>
            </Reveal>
          </div>

          <div className="mt-20 grid md:grid-cols-2 gap-10">
            {[
              {
                n: "01",
                t: "Why these botanicals.",
                b: "Every ingredient in MAN 360 earned its place from the classical Ayurvedic library — Shilajit, Ashwagandha, Safed Musli, Shatavari, Gokshura, Kauch Beej and their supporting botanicals. Chosen for a supportive daily role, not for a headline claim.",
              },
              {
                n: "02",
                t: "Why granules, not capsules.",
                b: "Granules preserve the honesty of a formulation — you see it, you smell it, you stir it into warm milk. A capsule hides what it holds. A granule respects the tradition it comes from.",
              },
              {
                n: "03",
                t: "Why we took our time.",
                b: "MAN 360 was refined slowly, deliberately, without the pressure to ship. The formulation waited until it earned our own conviction. We would rather launch late than launch loud.",
              },
              {
                n: "04",
                t: "Why quality before quantity.",
                b: "One flagship, done properly, is worth more than ten products done in a hurry. This is not our first attempt at MAN 360. It is our final one. And it will stay in a jar only if it deserves to.",
              },
            ].map((row, i) => (
              <Reveal key={row.n} delay={i * 100}>
                <article className="hk-card p-8 lg:p-10 h-full">
                  <div className="serif text-[36px] text-[var(--hk-gold-deep)] leading-none" style={{ paddingBottom: "0.04em" }}>{row.n}</div>
                  <h3 className="serif mt-5 text-[24px] sm:text-[28px] leading-[1.2] text-[var(--hk-ink)]" style={{ paddingBottom: "0.05em" }}>
                    {row.t}
                  </h3>
                  <p className="mt-4 text-[15px] leading-[1.95] text-[var(--hk-ink-2)]">{row.b}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 6 — The Shilajit Foundation (dedicated education)     */}
      {/* ============================================================ */}
      <section className="relative py-32 lg:py-44 overflow-hidden bg-[var(--hk-warm-ivory)]" data-testid="man360-shilajit">
        <BotanicalBackdrop variant="sideLeft" />
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
          <div className="max-w-3xl">
            <Reveal>
              <SectionEyebrow n="VI." label="The Shilajit Foundation" />
            </Reveal>
            <Reveal delay={120}>
              <h2 className="serif mt-8 text-[40px] sm:text-[58px] lg:text-[78px] leading-[1.08] tracking-tight text-[var(--hk-ink)]" style={{ paddingBottom: "0.06em" }}>
                Why <span className="italic-accent text-[var(--hk-orange-deep)]">Premium Shilajit</span>
                <br />
                is an important part of MAN 360.
              </h2>
            </Reveal>
            <Reveal delay={280}>
              <p className="mt-8 text-[17px] leading-[2] text-[var(--hk-ink-2)]">
                Shilajit has traditionally been regarded as one of the most respected
                <em> rasayana</em> ingredients in Ayurveda — valued for centuries as part of
                classical wellness practices. In MAN 360, purified Himalayan Shilajit is one of
                the foundation botanicals around which the formulation is built.
              </p>
            </Reveal>
            <Reveal delay={400}>
              <p className="mt-6 text-[17px] leading-[2] text-[var(--hk-ink-2)]">
                But we do not believe that formulation begins and ends with a single celebrated
                ingredient. A thoughtfully designed Ayurvedic composition combines complementary
                botanicals that have traditionally been used together — creating a more balanced
                approach than any single herb alone. That philosophy is what became MAN 360.
              </p>
            </Reveal>
          </div>

          {/* Centre resin, ringed with complementary botanicals */}
          <Reveal delay={200} className="mt-20 relative mx-auto" style={{ maxWidth: "min(680px, 96vw)" }}>
            <div className="relative mx-auto" style={{ width: "min(560px, 92vw)", height: "min(560px, 92vw)" }}>
              {/* engraved rings */}
              <div className="absolute inset-0 rounded-full border border-[var(--hk-border)]" />
              <div className="absolute inset-[10%] rounded-full border border-[var(--hk-border)]" />
              <div className="absolute inset-[24%] rounded-full border border-[var(--hk-gold)]/40" />

              {/* centre — Shilajit resin */}
              <div
                className="absolute inset-[30%] rounded-full flex items-center justify-center shadow-[0_25px_60px_-24px_rgba(30,35,31,0.5)] border border-[var(--hk-gold)]/60"
                style={{
                  background:
                    "radial-gradient(circle at 30% 30%, #6B4128, #2E1A0F 60%, #12080A)",
                }}
                aria-label="Purified Himalayan Shilajit resin"
              >
                <div className="text-center px-3">
                  <div className="serif italic text-[12px] text-[var(--hk-gold)]" style={{ paddingBottom: "0.06em" }}>the resin</div>
                  <div className="serif font-medium text-[26px] sm:text-[32px] leading-tight text-white" style={{ paddingBottom: "0.05em" }}>
                    Premium
                    <br />
                    Shilajit
                  </div>
                  <div className="mt-2 text-[9px] tracking-[0.32em] uppercase text-[var(--hk-gold)]/90">Himalayan Mineral Resin</div>
                </div>
              </div>

              {/* Complementary botanicals around resin */}
              {[
                "Ashwagandha", "Safed Musli", "Shatavari",
                "Gokshura", "Kauch Beej", "Vidarikand",
                "Tribulus", "Guduchi", "Manjishta",
              ].map((name, i, arr) => {
                const angle = (i / arr.length) * Math.PI * 2 - Math.PI / 2;
                const cx = 50 + 43 * Math.cos(angle);
                const cy = 50 + 43 * Math.sin(angle);
                return (
                  <div
                    key={name}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group hk-fade-up"
                    style={{ left: `${cx}%`, top: `${cy}%`, animationDelay: `${300 + i * 90}ms` }}
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white border border-[var(--hk-gold)]/50 shadow-[0_8px_18px_-8px_rgba(30,35,31,0.24)] flex items-center justify-center text-[var(--hk-orange-deep)] transition-transform duration-500 group-hover:scale-110 mx-auto">
                      <Leaf size={19} strokeWidth={1.4} />
                    </div>
                    <div
                      className="mt-2 whitespace-nowrap text-[10px] tracking-[0.2em] uppercase text-[var(--hk-ink-2)] group-hover:text-[var(--hk-orange-deep)] transition-colors text-center"
                      style={{ paddingBottom: "0.06em" }}
                    >
                      {name}
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>

          {/* Comparison — one ingredient vs a formulation */}
          <div className="mt-24">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto">
                <div className="label-eyebrow">A difference in philosophy</div>
                <h3 className="serif mt-4 text-[30px] sm:text-[40px] lg:text-[48px] leading-[1.15] tracking-tight text-[var(--hk-ink)]" style={{ paddingBottom: "0.06em" }}>
                  One ingredient <span className="italic-accent text-[var(--hk-orange-deep)]">vs</span> a thoughtfully crafted formulation.
                </h3>
              </div>
            </Reveal>

            <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
              <Reveal delay={100}>
                <div className="hk-card p-8 lg:p-10 h-full">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)] flex items-center justify-center text-[var(--hk-orange-deep)]">
                      <Mountain size={17} strokeWidth={1.6} />
                    </div>
                    <div className="serif text-[22px] text-[var(--hk-ink)] leading-tight" style={{ paddingBottom: "0.05em" }}>Premium Shilajit</div>
                  </div>
                  <ul className="mt-6 space-y-3 text-[15px] leading-[1.85] text-[var(--hk-ink-2)]">
                    <li className="flex gap-3"><LeafGlyph className="mt-1 text-[var(--hk-gold-deep)] w-3.5 h-3.5 shrink-0" /> A single botanical</li>
                    <li className="flex gap-3"><LeafGlyph className="mt-1 text-[var(--hk-gold-deep)] w-3.5 h-3.5 shrink-0" /> Traditionally respected in Ayurveda</li>
                    <li className="flex gap-3"><LeafGlyph className="mt-1 text-[var(--hk-gold-deep)] w-3.5 h-3.5 shrink-0" /> Widely recognised as a rasayana ingredient</li>
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={220}>
                <div
                  className="p-8 lg:p-10 h-full rounded-2xl relative overflow-hidden"
                  style={{
                    background: "linear-gradient(140deg, rgba(217,117,53,0.10), rgba(201,169,97,0.10))",
                    border: "1px solid rgba(184, 94, 36, 0.35)",
                  }}
                >
                  <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[var(--hk-orange)]/10 blur-2xl" aria-hidden="true" />
                  <div className="relative">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[var(--hk-orange)] text-white border border-[var(--hk-orange-deep)] flex items-center justify-center">
                        <Sparkles size={17} strokeWidth={1.6} />
                      </div>
                      <div className="serif text-[22px] text-[var(--hk-ink)] leading-tight" style={{ paddingBottom: "0.05em" }}>MAN 360</div>
                    </div>
                    <ul className="mt-6 space-y-3 text-[15px] leading-[1.85] text-[var(--hk-ink-2)]">
                      <li className="flex gap-3"><LeafGlyph className="mt-1 text-[var(--hk-orange-deep)] w-3.5 h-3.5 shrink-0" /> Premium Shilajit at its foundation</li>
                      <li className="flex gap-3"><LeafGlyph className="mt-1 text-[var(--hk-orange-deep)] w-3.5 h-3.5 shrink-0" /> Plus 13 thoughtfully selected complementary botanicals</li>
                      <li className="flex gap-3"><LeafGlyph className="mt-1 text-[var(--hk-orange-deep)] w-3.5 h-3.5 shrink-0" /> Premium herbal granulated formulation</li>
                      <li className="flex gap-3"><LeafGlyph className="mt-1 text-[var(--hk-orange-deep)] w-3.5 h-3.5 shrink-0" /> Designed around a complete men's wellness philosophy</li>
                      <li className="flex gap-3"><LeafGlyph className="mt-1 text-[var(--hk-orange-deep)] w-3.5 h-3.5 shrink-0" /> Created for modern, disciplined lifestyles</li>
                    </ul>
                  </div>
                </div>
              </Reveal>
            </div>

            <Reveal delay={400} className="mt-16 text-center max-w-3xl mx-auto">
              <p className="serif italic text-[22px] sm:text-[28px] lg:text-[32px] leading-[1.35] text-[var(--hk-ink)]" style={{ paddingBottom: "0.06em" }}>
                At HerbKranti, we believe one great ingredient <br className="hidden sm:block" />
                deserves the <span className="text-[var(--hk-orange-deep)]">company of other great ingredients</span>.
                <br />
                That belief became MAN 360.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 7 — The HerbKranti Night Ritual                       */}
      {/* ============================================================ */}
      <section className="relative py-32 lg:py-44 overflow-hidden bg-[#211F1B] text-[#EDE4CE]" data-testid="man360-ritual">
        <div
          aria-hidden="true"
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-[160px] opacity-20 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(217,117,53,0.5), transparent 70%)" }}
        />
        <Grain />

        <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <Reveal>
              <div className="inline-flex items-center gap-3">
                <div className="w-14 h-px bg-[var(--hk-gold)]/60" />
                <div className="text-[11px] tracking-[0.42em] uppercase text-[var(--hk-gold)]">VII. The HerbKranti Night Ritual</div>
                <div className="w-14 h-px bg-[var(--hk-gold)]/60" />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="serif mt-8 text-[42px] sm:text-[60px] lg:text-[84px] leading-[1.08] tracking-[-0.02em] text-white font-medium" style={{ paddingBottom: "0.06em" }}>
                Made of milk, ghee <br className="hidden sm:block" />
                <span className="italic text-[var(--hk-orange)]">and quiet patience.</span>
              </h2>
            </Reveal>
            <Reveal delay={280}>
              <p className="mt-10 text-[17px] leading-[2] text-white/75 max-w-2xl mx-auto">
                A ritual is different from a routine. A routine is what you do. A ritual is how
                you do it — with intention, with care, with the small, deliberate acts that turn
                a nightly practice into a lifelong discipline.
              </p>
            </Reveal>
          </div>

          <div className="mt-20 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Warm the milk.",   b: "Approximately 150 ml. Not boiling — warmed, gently, until it comforts the hand.", Icon: Sparkles },
              { n: "02", t: "Add desi ghee.",   b: "Two teaspoons of pure desi ghee. The traditional carrier — never a step to skip.", Icon: Flame },
              { n: "03", t: "Add MAN 360.",    b: "Five grams of premium herbal granules. Free-flowing, aromatic, unhurried.", Icon: Leaf },
              { n: "04", t: "Stir thoroughly.", b: "Until the granules are evenly blended into the milk. Slow, circular, considered.", Icon: Repeat },
              { n: "05", t: "Take it before bed.", b: "The night is the body's window for repair. This ritual belongs there.", Icon: Moon },
              { n: "06", t: "The last of the day.", b: "For best results, this should be the final nutritional intake of your day.", Icon: Shield },
            ].map((row, i) => {
              const { Icon } = row;
              return (
                <Reveal key={row.n} delay={i * 90}>
                  <div className="p-8 rounded-2xl border border-white/12 bg-white/[0.03] backdrop-blur-sm h-full">
                    <div className="flex items-center justify-between">
                      <div className="serif italic text-[13px] text-[var(--hk-gold)]">— step {row.n}</div>
                      <div className="w-10 h-10 rounded-full bg-white/5 border border-white/12 flex items-center justify-center text-[var(--hk-orange)]">
                        <Icon size={18} strokeWidth={1.4} />
                      </div>
                    </div>
                    <h3 className="serif mt-6 text-[22px] leading-[1.25] text-white" style={{ paddingBottom: "0.05em" }}>{row.t}</h3>
                    <p className="mt-3 text-[14px] leading-[1.85] text-white/70">{row.b}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={700} className="mt-16 text-center max-w-2xl mx-auto">
            <p className="serif italic text-[22px] sm:text-[28px] leading-[1.35] text-white" style={{ paddingBottom: "0.06em" }}>
              A ritual is not a task.
              <br />
              A ritual is a <span className="text-[var(--hk-orange)]">quiet promise</span> to your future self.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SECTION 7 — The 360 Philosophy (radial wheel)                 */}
      {/* ============================================================ */}
      <section className="relative py-32 lg:py-44 overflow-hidden" data-testid="man360-philosophy-wheel">
        <BotanicalBackdrop variant="corners" />
        <div className="relative mx-auto max-w-6xl px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <Reveal>
              <SectionEyebrow n="VIII." label="The 360 Philosophy" />
            </Reveal>
            <Reveal delay={120}>
              <h2 className="serif mt-8 text-[40px] sm:text-[58px] lg:text-[78px] leading-[1.08] tracking-tight text-[var(--hk-ink)]" style={{ paddingBottom: "0.06em" }}>
                Wellness is not a <span className="italic-accent text-[var(--hk-orange-deep)]">single line.</span>
                <br />
                It is a full circle.
              </h2>
            </Reveal>
            <Reveal delay={280}>
              <p className="mt-8 text-[17px] leading-[2] text-[var(--hk-ink-2)] max-w-2xl mx-auto">
                MAN 360 is not named for its ingredients. It is named for how a man is actually
                built — as a whole, connected system. Move one part, and the rest listens.
                Ignore one part, and the rest slowly gives way. Nine aspects, one circle, one
                philosophy.
              </p>
            </Reveal>
          </div>

          <Reveal delay={200} className="mt-20 flex justify-center pb-56 lg:pb-48">
            <PhilosophyWheel />
          </Reveal>

          <Reveal delay={800} className="mt-16 text-center max-w-2xl mx-auto">
            <p className="serif italic text-[22px] sm:text-[28px] leading-[1.35] text-[var(--hk-ink)]" style={{ paddingBottom: "0.06em" }}>
              MAN 360.
              <br />
              A daily discipline for a <span className="text-[var(--hk-orange-deep)]">complete life</span>.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Man360Story;
